import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { COMPANY, scrollToId } from "../../lib/site";

const MENTIONS = [
    ["Éditeur du site", `${COMPANY.legal} — Société par actions simplifiée (SAS), SIREN ${COMPANY.siren}, SIRET ${COMPANY.siret}, ${COMPANY.rcs}. Siège social : ${COMPANY.address1}, ${COMPANY.address2}.`],
    ["Directeur de la publication", "[À COMPLÉTER]"],
    ["Contact", `Téléphone : ${COMPANY.phone} — Email : ${COMPANY.email}`],
    ["Hébergement", "[HÉBERGEUR À COMPLÉTER]"],
];

const PRIVACY = [
    ["Données collectées", "Le formulaire de contact collecte vos nom, société, coordonnées (téléphone, email) ainsi que les informations relatives à votre chantier (localisation, besoin, description)."],
    ["Finalité", "Ces informations sont utilisées exclusivement par NEBOORZY pour vous recontacter au sujet de votre demande et, le cas échéant, organiser un rendez-vous commercial."],
    ["Base légale", "Le traitement repose sur votre consentement (case à cocher) et sur l'exécution de mesures précontractuelles."],
    ["Conservation", "Vos données sont conservées pendant la durée nécessaire au traitement de votre demande, puis archivées ou supprimées."],
    ["Vos droits", `Conformément au RGPD, vous disposez de droits d'accès, de rectification, d'effacement et d'opposition. Pour les exercer : ${COMPANY.email}.`],
    ["Cookies", "Ce site n'utilise pas de cookies publicitaires. Des mesures d'audience techniques peuvent être mises en œuvre ; aucune donnée n'est cédée à des tiers à des fins commerciales."],
];

const LegalModal = ({ title, sections, onClose, testid }) => (
    <motion.div
        data-testid={testid}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-end justify-center bg-ink/80 p-0 backdrop-blur-sm sm:items-center sm:p-6"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={title}
    >
        <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl bg-white p-7 text-ink sm:rounded-2xl sm:p-10"
        >
            <div className="mb-6 flex items-center justify-between">
                <h3 className="font-display text-2xl font-bold">{title}</h3>
                <button
                    type="button"
                    data-testid={`${testid}-close`}
                    onClick={onClose}
                    className="grid h-10 w-10 place-items-center rounded-full border border-zinc-200 text-ink transition-colors hover:bg-zinc-100"
                    aria-label="Fermer"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>
            <div className="space-y-5">
                {sections.map(([h, t]) => (
                    <div key={h}>
                        <h4 className="text-sm font-bold uppercase tracking-wide text-signal">{h}</h4>
                        <p className="mt-1.5 text-sm leading-relaxed text-zinc-600">{t}</p>
                    </div>
                ))}
            </div>
        </motion.div>
    </motion.div>
);

export const Footer = () => {
    const [modal, setModal] = useState(null);

    return (
        <footer id="contact" data-testid="legal-footer" className="border-t border-white/10 bg-ink pb-24 pt-20 sm:pb-20">
            <div className="mx-auto max-w-7xl px-5 lg:px-8">
                <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr]">
                    <div>
                        <div className="flex items-center gap-2.5">
                            <span className="grid h-9 w-9 place-items-center rounded-md bg-signal font-display text-xl font-black text-ink">
                                N
                            </span>
                            <span className="font-display text-2xl font-extrabold tracking-tight text-white">
                                NEBOORZY
                            </span>
                        </div>
                        <p className="mt-5 max-w-sm text-sm leading-relaxed text-zinc-400">
                            Location de matériel et équipements de chantier, location de véhicules
                            sans chauffeur, travaux BTP, rénovation, électricité, plomberie et
                            carrelage — pour les professionnels.
                        </p>
                        <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
                            Zone d'intervention : <span data-testid="footer-zone" className="text-zinc-300">{COMPANY.zone}</span>
                        </p>
                    </div>
                    <div>
                        <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Contact</h3>
                        <address className="mt-5 space-y-3 text-sm not-italic leading-relaxed text-zinc-300">
                            <p data-testid="footer-address">
                                Siège social : {COMPANY.address1}
                                <br />
                                {COMPANY.address2}
                            </p>
                            <p>
                                Téléphone :{" "}
                                <span data-testid="footer-phone" className="text-zinc-400">{COMPANY.phone}</span>
                            </p>
                            <p>
                                Email :{" "}
                                <span data-testid="footer-email" className="text-zinc-400">{COMPANY.email}</span>
                            </p>
                        </address>
                    </div>
                    <div>
                        <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-signal">Informations légales</h3>
                        <ul className="mt-5 space-y-2.5 text-sm text-zinc-400">
                            <li>SAS — {COMPANY.legal}</li>
                            <li data-testid="footer-siren">SIREN : {COMPANY.siren}</li>
                            <li data-testid="footer-siret">SIRET : {COMPANY.siret}</li>
                            <li data-testid="footer-rcs">{COMPANY.rcs}</li>
                        </ul>
                        <div className="mt-6 flex flex-col items-start gap-2.5">
                            <a
                                data-testid="footer-link-plaquette"
                                href="/plaquette-commerciale-neboorzy.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                className="text-sm font-medium text-zinc-300 underline-offset-4 transition-colors hover:text-signal hover:underline"
                            >
                                Plaquette commerciale
                            </a>
                            <button
                                type="button"
                                data-testid="footer-link-mentions-legales"
                                onClick={() => setModal("mentions")}
                                className="text-sm font-medium text-zinc-300 underline-offset-4 transition-colors hover:text-signal hover:underline"
                            >
                                Mentions légales
                            </button>
                            <button
                                type="button"
                                data-testid="footer-link-confidentialite"
                                onClick={() => setModal("privacy")}
                                className="text-sm font-medium text-zinc-300 underline-offset-4 transition-colors hover:text-signal hover:underline"
                            >
                                Politique de confidentialité
                            </button>
                            <button
                                type="button"
                                data-testid="footer-cta-commercial"
                                onClick={() => scrollToId("besoin")}
                                className="mt-2 text-sm font-bold text-signal underline-offset-4 transition-colors hover:text-white hover:underline"
                            >
                                Demander le passage d'un commercial
                            </button>
                        </div>
                    </div>
                </div>
                <div className="mt-16 flex flex-col gap-2 border-t border-white/10 pt-7 text-xs text-zinc-500 sm:flex-row sm:items-center sm:justify-between">
                    <p>© {new Date().getFullYear()} {COMPANY.name} — Tous droits réservés.</p>
                    <p className="font-mono uppercase tracking-[0.2em]">Interventions dans toute la France</p>
                </div>
            </div>
            <AnimatePresence>
                {modal === "mentions" && (
                    <LegalModal title="Mentions légales" sections={MENTIONS} onClose={() => setModal(null)} testid="modal-mentions-legales" />
                )}
                {modal === "privacy" && (
                    <LegalModal title="Politique de confidentialité" sections={PRIVACY} onClose={() => setModal(null)} testid="modal-confidentialite" />
                )}
            </AnimatePresence>
        </footer>
    );
};
